import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { LucideCoins, ZapIcon } from "lucide-react";
import { Link } from "react-router";
import {
  useUserAndCredits,
  timeProgressPercentToCreditsRenewal,
  formatDurationRemainingToCreditsRenewal,
} from "@/components/utils/credits";

export const AICreditDisplay = ({
  isCollapsed,
  isShowWhenPremium,
  showWaitTime = true,
  showUpgradeButton = true,
  creditsIncrease = 0,
}: {
  isCollapsed: boolean;
  showWaitTime?: boolean;
  isShowWhenPremium: boolean;
  showUpgradeButton?: boolean;
  creditsIncrease?: number;
}) => {
  const { isLoggedIn, isLoading, hasUpgraded } = useUserAndCredits();

  if (!isShowWhenPremium && hasUpgraded) {
    return <></>;
  }

  if (!isLoggedIn || isLoading) {
    return <></>;
  }

  return (
    <DisplayAICredits
      isCollapsed={isCollapsed}
      showWaitTime={showWaitTime}
      creditsIncrease={creditsIncrease}
      showUpgradeButton={showUpgradeButton}
    />
  );
};

const DisplayAICredits = ({
  isCollapsed,
  showWaitTime = true,
  showUpgradeButton = true,
  creditsIncrease = 0,
}: {
  isCollapsed: boolean;
  showWaitTime?: boolean;
  showUpgradeButton?: boolean;
  creditsIncrease?: number;
}) => {
  const {
    isError,
    isLoggedIn,
    isLoading,
    plan,
    hasUpgraded,
    isUnlimited,
    maxDailyCredits,
    maxMonthlyCredits,
    remainingCredits,
    timeToNextRenewal,
  } = useUserAndCredits();

  if (!isLoggedIn) {
    return <></>;
  }

  if (isError) {
    return <div>Error loading credits</div>;
  }

  if (isLoading) {
    return <></>;
  }

  if (isUnlimited) {
    if (isCollapsed) {
      return <></>;
    }
    return (
      <div className="w-full flex flex-col gap-2">
        <div className="flex justify-between items-center text-body-600 pt-2">
          <div className="flex items-center gap-1 font-medium">
            <LucideCoins className="mr-1 h-5 w-5" />
            Unlimited AI credits
          </div>
        </div>
      </div>
    );
  }

  const totalCredits = maxDailyCredits ?? maxMonthlyCredits ?? Infinity;

  if (isLoading) {
    return (
      <div className="w-full flex flex-col gap-2">
        {isCollapsed ? (
          <div className="flex flex-col items-center justify-center my-2 text-subtitle-800">
            <LucideCoins className="mr-1 h-5 w-5" />
            <div>
              <div className="text-sm text-right flex items-center justify-center mt-1">
                <span className="font-semibold">
                  <Skeleton className="h-3 w-4" />
                </span>
                /{totalCredits}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center text-subtitle-800 pt-2">
              <div className="flex items-center gap-1 font-medium">
                <LucideCoins className="mr-1 h-5 w-5" />
                {plan === "basic" ? "Daily " : ""}AI credits
              </div>
              <div className="text-sm text-right flex items-center">
                <Skeleton className="w-[30px] h-3 mr-1" />/ {totalCredits}
              </div>
            </div>
            <div className="w-full bg-secondary-200 rounded-xl h-2 mb-4">
              <Skeleton className="h-full w-full" />
            </div>
          </>
        )}
        {!hasUpgraded && showUpgradeButton ? (
          <Link to="/upgrade">
            <Button variant="primary" size={isCollapsed ? "iconMd" : "md"}>
              <ZapIcon className={cn("h-icon-md w-icon-md")} />
              {isCollapsed ? "" : "Upgrade"}
            </Button>
          </Link>
        ) : (
          <></>
        )}
      </div>
    );
  }

  const increasedCredits = remainingCredits + creditsIncrease;

  const creditsIncreaseExceedsTotalCredits =
    remainingCredits <= totalCredits && increasedCredits > totalCredits;

  const usagePercentage = Math.max(
    0,
    Math.min(100, (remainingCredits / totalCredits) * 100)
  );
  const usageIncreasedPercentage = Math.max(
    0,
    Math.min(100, ((remainingCredits + creditsIncrease) / totalCredits) * 100)
  );
  const supplementaryUsagePercentage = Math.max(
    0,
    Math.min(100, (remainingCredits / totalCredits - 1) * 100)
  );
  const supplementaryIncreasedUsagePercentage = Math.max(
    0,
    creditsIncreaseExceedsTotalCredits
      ? Math.min(100, (creditsIncrease / totalCredits) * 100)
      : 0,
    Math.min(
      100,
      ((remainingCredits + creditsIncrease) / totalCredits - 1) * 100
    )
  );

  const progressUntilCreditsRenew = timeProgressPercentToCreditsRenewal(plan);

  return (
    <div className="w-full flex flex-col gap-2 text-body-600">
      {!isCollapsed ? (
        <div className="flex justify-between items-center text-body-600 pt-2">
          <div className="flex items-center gap-1 font-medium">
            <LucideCoins className="mr-1 h-5 w-5" />
            {plan === "basic" ? "Daily " : "Monthly "}AI credits
          </div>
          <div className="text-sm text-right">
            {remainingCredits > 0 || !showWaitTime ? (
              <>
                <span className="text-sm font-semibold">
                  {remainingCredits}
                </span>
                /{totalCredits}
              </>
            ) : (
              <span className="text-sm font-semibold">
                {formatDurationRemainingToCreditsRenewal(
                  timeToNextRenewal ?? {},
                  2
                )}
              </span>
            )}
          </div>
        </div>
      ) : !hasUpgraded ? (
        <></>
      ) : (
        <div className="flex flex-col items-center justify-center my-2">
          <LucideCoins className="mr-1 h-5 w-5 text-subtitle-800" />
          <div>
            {remainingCredits > 0 || !showWaitTime ? (
              <div className="text-sm">
                <span className="font-semibold">{remainingCredits}</span>/
                {totalCredits}
              </div>
            ) : (
              <span className="text-sm font-semibold">
                {formatDurationRemainingToCreditsRenewal(
                  timeToNextRenewal ?? {},
                  1
                )}
              </span>
            )}
          </div>
        </div>
      )}
      {isCollapsed ? (
        <></>
      ) : (
        <div className="w-full bg-secondary-200 rounded-xl h-2 mb-4 relative">
          {remainingCredits <= 0 && showWaitTime ? (
            <div
              className="absolute top-0 left-0 bg-secondary-300 h-full rounded-xl transition-all duration-500 ease-in-out"
              style={{ width: `${progressUntilCreditsRenew}%` }}
            />
          ) : (
            <></>
          )}
          {!creditsIncreaseExceedsTotalCredits ? (
            <div
              className="absolute top-0 left-0 bg-primary-700 h-full rounded-xl transition-all duration-500 ease-in-out"
              style={{ width: `${usageIncreasedPercentage}%` }}
            />
          ) : (
            <></>
          )}
          <div
            className={cn(
              "absolute top-0 left-0 bg-primary-400 h-full rounded-xl transition-all duration-500 ease-in-out"
            )}
            style={{
              width: `${
                creditsIncreaseExceedsTotalCredits ? 100 : usagePercentage
              }%`,
            }}
          />
          <div
            className="absolute top-0 left-0 bg-primary-700 h-full rounded-xl transition-all duration-500 ease-in-out"
            style={{ width: `${supplementaryIncreasedUsagePercentage}%` }}
          />
          <div
            className="absolute top-0 left-0 bg-primary-500 h-full rounded-xl transition-all duration-500 ease-in-out"
            style={{ width: `${supplementaryUsagePercentage}%` }}
          />
        </div>
      )}
      {!hasUpgraded && showUpgradeButton ? (
        <Link to="/upgrade" className="w-full">
          <Button
            variant="primary"
            size={isCollapsed ? "iconMd" : "md"}
            className={cn(isCollapsed ? "" : "w-full")}
          >
            <ZapIcon
              className={cn("h-icon-md w-icon-md", isCollapsed ? "" : "mr-1")}
            />
            {isCollapsed ? "" : "Upgrade"}
          </Button>
        </Link>
      ) : (
        <></>
      )}
    </div>
  );
};

export const useAICredits = () => {
  const queryClient = useQueryClient();

  const updateCredits = (newUsage: number) => {
    queryClient.setQueryData(["user-usage"], { data: newUsage });
  };

  const reduceCredits = (usedCredits: number) => {
    queryClient.setQueryData(
      ["user-usage"],
      (oldData: { data: number } | undefined) => {
        if (!oldData) {
          return undefined;
        }

        return { data: oldData.data + usedCredits };
      }
    );
  };

  return {
    updateCredits,
    reduceCredits,
    refetchCredits: () =>
      queryClient.invalidateQueries({ queryKey: ["user-usage"] }),
  };
};
